import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import QRCode from 'qrcode';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import type { LoginResponse } from '../lib/types';
import { Alert, Button, Input, errorMessage } from '../components/ui';

type Step =
  | { kind: 'credentials' }
  | { kind: 'enroll'; mfaToken: string }
  | { kind: 'verify'; mfaToken: string; recovery: boolean }
  | { kind: 'codes'; codes: string[]; accessToken: string };

export function Brand() {
  return (
    <div className="flex items-center gap-2">
      <span
        className="grid size-8 place-items-center rounded-md bg-indigo-600 text-sm font-bold text-white"
        aria-hidden
      >
        EC
      </span>
      <span className="text-lg font-semibold tracking-tight text-slate-900">EndCopilot</span>
    </div>
  );
}

function Shell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
}) {
  return (
    <main className="flex min-h-dvh items-center justify-center bg-slate-50 px-4 py-10">
      <div className="w-full max-w-md">
        <Brand />
        <div className="mt-6 rounded-lg border border-slate-200 bg-white p-6 shadow-xs">
          <h1 className="text-lg font-semibold text-slate-900">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-slate-600">{subtitle}</p>}
          <div className="mt-5">{children}</div>
        </div>
        <p className="mt-4 text-center text-xs text-slate-500">
          Smile Tecnologia · acesso restrito a usuários autorizados
        </p>
      </div>
    </main>
  );
}

export function LoginPage() {
  const session = useSession();
  const [step, setStep] = useState<Step>({ kind: 'credentials' });

  const finish = async (res: Extract<LoginResponse, { status: 'ok' }>) => {
    if (res.recoveryCodes?.length)
      setStep({ kind: 'codes', codes: res.recoveryCodes, accessToken: res.accessToken });
    else await session.completeLogin(res.accessToken);
  };

  switch (step.kind) {
    case 'credentials':
      return (
        <Credentials
          onResult={async (r) => {
            if (r.status === 'ok') return finish(r);
            setStep(
              r.stage === 'enroll'
                ? { kind: 'enroll', mfaToken: r.mfaToken }
                : { kind: 'verify', mfaToken: r.mfaToken, recovery: false },
            );
          }}
        />
      );
    case 'enroll':
      return (
        <Enroll
          mfaToken={step.mfaToken}
          onDone={finish}
          onCancel={() => setStep({ kind: 'credentials' })}
        />
      );
    case 'verify':
      return (
        <Verify
          mfaToken={step.mfaToken}
          recovery={step.recovery}
          onToggle={() => setStep({ ...step, recovery: !step.recovery })}
          onDone={finish}
          onCancel={() => setStep({ kind: 'credentials' })}
        />
      );
    case 'codes':
      return (
        <RecoveryCodes
          codes={step.codes}
          onContinue={() => session.completeLogin(step.accessToken)}
        />
      );
  }
}

function Credentials({ onResult }: { onResult: (r: LoginResponse) => Promise<void> }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onResult(
        await api.post<LoginResponse>('/v1/auth/login', { email, password }, { anonymous: true }),
      );
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <Shell title="Entrar" subtitle="Use o e-mail e a senha cadastrados pela sua empresa.">
      <form onSubmit={submit} className="space-y-4" noValidate>
        {error && <Alert tone="error">{error}</Alert>}
        <Input
          label="E-mail"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Input
          label="Senha"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Button type="submit" busy={busy} className="w-full" disabled={!email || !password}>
          Entrar
        </Button>
      </form>
    </Shell>
  );
}

function CodeForm({
  label,
  hint,
  pattern,
  onSubmit,
  submitLabel,
}: {
  label: string;
  hint: string;
  pattern: 'totp' | 'recovery';
  onSubmit: (code: string) => Promise<void>;
  submitLabel: string;
}) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valid =
    pattern === 'totp' ? /^\d{6}$/.test(code) : code.replace(/[\s-]/g, '').length === 10;

  return (
    <form
      className="space-y-4"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
          await onSubmit(code.trim());
        } catch (err) {
          setError(errorMessage(err));
          setBusy(false);
        }
      }}
    >
      {error && <Alert tone="error">{error}</Alert>}
      <Input
        label={label}
        hint={hint}
        value={code}
        onChange={(e) =>
          setCode(
            pattern === 'totp'
              ? e.target.value.replace(/\D/g, '').slice(0, 6)
              : e.target.value.slice(0, 20),
          )
        }
        inputMode={pattern === 'totp' ? 'numeric' : 'text'}
        autoComplete="one-time-code"
        autoFocus
        className="[&_input]:font-mono [&_input]:tracking-widest"
      />
      <Button type="submit" busy={busy} disabled={!valid} className="w-full">
        {submitLabel}
      </Button>
    </form>
  );
}

function Enroll({
  mfaToken,
  onDone,
  onCancel,
}: {
  mfaToken: string;
  onDone: (r: Extract<LoginResponse, { status: 'ok' }>) => Promise<void>;
  onCancel: () => void;
}) {
  const [data, setData] = useState<{ secret: string; qr: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .post<{ otpauthUri: string; secret: string }>('/v1/auth/mfa/enroll', {}, { bearer: mfaToken })
      .then(async (r) => {
        // QR gerado localmente (SVG): o segredo não é enviado a nenhum serviço externo.
        const svg = await QRCode.toString(r.otpauthUri, {
          type: 'svg',
          margin: 1,
          errorCorrectionLevel: 'M',
        });
        if (alive)
          setData({
            secret: r.secret,
            qr: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
          });
      })
      .catch((e) => alive && setError(errorMessage(e)));
    return () => {
      alive = false;
    };
  }, [mfaToken]);

  return (
    <Shell
      title="Ative a verificação em duas etapas"
      subtitle="Seu perfil exige um app autenticador (Google Authenticator, Microsoft Authenticator, 1Password ou similar)."
    >
      {error && (
        <div className="space-y-3">
          <Alert tone="error">{error}</Alert>
          <Button variant="secondary" onClick={onCancel}>
            Voltar
          </Button>
        </div>
      )}
      {!data && !error && <p className="text-sm text-slate-500">Preparando…</p>}
      {data && (
        <div className="space-y-5">
          <ol className="list-decimal space-y-1 pl-5 text-sm text-slate-700">
            <li>No app autenticador, adicione uma conta lendo o QR Code.</li>
            <li>Digite o código de 6 dígitos que o app mostrar.</li>
          </ol>
          <div className="flex flex-col items-center gap-3">
            <img
              src={data.qr}
              alt="QR Code para cadastrar o EndCopilot no app autenticador"
              className="size-48 rounded-md ring-1 ring-slate-200"
            />
            <details className="w-full text-sm">
              <summary className="cursor-pointer text-indigo-700">
                Não consegue ler o QR? Digite a chave
              </summary>
              <code
                className="mt-2 block rounded bg-slate-100 p-2 font-mono text-xs break-all"
                data-testid="totp-secret"
              >
                {data.secret.match(/.{1,4}/g)?.join(' ')}
              </code>
            </details>
          </div>
          <CodeForm
            label="Código do app"
            hint="6 dígitos, renovados a cada 30 segundos."
            pattern="totp"
            submitLabel="Ativar e entrar"
            onSubmit={async (code) =>
              onDone(await api.post('/v1/auth/mfa/verify', { code }, { bearer: mfaToken }))
            }
          />
        </div>
      )}
    </Shell>
  );
}

function Verify({
  mfaToken,
  recovery,
  onToggle,
  onDone,
  onCancel,
}: {
  mfaToken: string;
  recovery: boolean;
  onToggle: () => void;
  onDone: (r: Extract<LoginResponse, { status: 'ok' }>) => Promise<void>;
  onCancel: () => void;
}) {
  return (
    <Shell
      title="Verificação em duas etapas"
      subtitle={
        recovery
          ? 'Use um dos códigos de recuperação que você guardou.'
          : 'Digite o código do seu app autenticador.'
      }
    >
      {recovery ? (
        <CodeForm
          key="recovery"
          label="Código de recuperação"
          hint="Formato xxxxx-xxxxx. Cada código vale uma única vez."
          pattern="recovery"
          submitLabel="Entrar"
          onSubmit={async (code) =>
            onDone(await api.post('/v1/auth/mfa/recovery', { code }, { bearer: mfaToken }))
          }
        />
      ) : (
        <CodeForm
          key="totp"
          label="Código"
          hint="6 dígitos."
          pattern="totp"
          submitLabel="Verificar"
          onSubmit={async (code) =>
            onDone(await api.post('/v1/auth/mfa/verify', { code }, { bearer: mfaToken }))
          }
        />
      )}
      <div className="mt-4 flex flex-wrap justify-between gap-2 text-sm">
        <button type="button" className="text-indigo-700 hover:underline" onClick={onToggle}>
          {recovery ? 'Usar o app autenticador' : 'Perdi o acesso ao app'}
        </button>
        <button type="button" className="text-slate-600 hover:underline" onClick={onCancel}>
          Voltar
        </button>
      </div>
    </Shell>
  );
}

export function RecoveryCodeList({ codes }: { codes: string[] }) {
  return (
    <ul
      className="grid grid-cols-2 gap-2 rounded-md bg-slate-50 p-3 font-mono text-sm ring-1 ring-slate-200"
      aria-label="Códigos de recuperação"
    >
      {codes.map((c) => (
        <li key={c}>{c}</li>
      ))}
    </ul>
  );
}

function RecoveryCodes({
  codes,
  onContinue,
}: {
  codes: string[];
  onContinue: () => Promise<void>;
}) {
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <Shell
      title="Guarde seus códigos de recuperação"
      subtitle="Se perder o celular, cada código permite um acesso. Eles não serão mostrados de novo."
    >
      <div className="space-y-4">
        <RecoveryCodeList codes={codes} />
        <Button
          variant="secondary"
          onClick={() => void navigator.clipboard?.writeText(codes.join('\n'))}
        >
          Copiar códigos
        </Button>
        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={saved}
            onChange={(e) => setSaved(e.target.checked)}
          />
          Guardei os códigos num local seguro (cofre de senhas ou papel guardado).
        </label>
        <Button
          className="w-full"
          disabled={!saved}
          busy={busy}
          onClick={async () => {
            setBusy(true);
            await onContinue();
          }}
        >
          Continuar
        </Button>
      </div>
    </Shell>
  );
}
